var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ScriptPropertyType, UpdateFacetOnModelMessage, PersistentDataFacet, hashCode, MulticastDelegate1, isPromise } from "flavr-common";
import { Vector2, Vector3, Vector4, Quaternion, Color } from "flavr-math";
import { getModelFromAbsolutePath, modelPool } from "@modules/model";
import { Property } from "./Property";
import { ScriptModel } from "./ScriptModel";
import { ModelScriptFunctions } from "./ModelScriptFunctions";
import { SCRIPT_FUNCTION_NAMES_SET } from "../script/ScriptManager";
import { scriptBusService } from "../services/ScriptBusService";
import { scriptService } from "../services/ScriptService";
const tmpVector31 = new Vector3();
const tmpVector32 = new Vector3();
const VALID_DEFAULT_PROPERTY_TYPES = ["undefined", "number", "boolean", "string"];
/**
Script example:

return class Model extends ModelBase {
  onInit() {
    console.log("onInit");
  }
  onDestroy() {
    console.log(onDestroy);
  }
  onUpdate() {
    console.log("onUpdate");
  }
}

Order of Execution:

onEnterRoom()
  v
onStart()
  v <--------
onUpdate()  |
  v ---------
onLeaveRoom()
  v
onDestroy()
*/
export class ModelScript {
    constructor(modelUID, scriptId, context, room, scriptIdx) {
        this.modelUID = modelUID;
        this.scriptId = scriptId;
        this.context = context;
        this.room = room;
        this.scriptIdx = scriptIdx;
        this.autoExecute = true;
        this.active = false;
        this.isRegisteredInRoom = false;
        this.isInitialized = false;
        this.isDestroyed = false;
        this.createdAt = Date.now();
        this.onDestroyDelegate = new MulticastDelegate1();
        this.callAfterStart = new Map();
        this.context.scripts.set(this.scriptIdx, this);
    }
    /// Getters ///
    get uid() {
        return this.modelUID;
    }
    get scriptName() {
        if (this.script === undefined)
            return undefined;
        return this.script.constructor.name;
    }
    get scriptInstance() {
        return this.script;
    }
    isReady() {
        return this.script !== undefined;
    }
    syncExecutionState(autoExecute = true, active = false) {
        const wasExecutable = this.autoExecute || this.active;
        this.autoExecute = autoExecute;
        this.active = active;
        const shouldExecute = this.autoExecute || this.active;
        if (!shouldExecute || wasExecutable === shouldExecute)
            return;
        if (this.script !== undefined)
            this.registerScriptInRoom(this.script);
    }
    setCurrentContext() {
        if (scriptService.context === undefined)
            return;
        scriptService.context.currentScriptIdx = this.scriptIdx;
    }
    /// Script life-cycle ///
    firstInit() {
        var _a, _b;
        if (this.isDestroyed)
            delete this.isDestroyed;
        this.forwardFunctionCallToScript("onFirstInit", (_b = (_a = this.script) === null || _a === void 0 ? void 0 : _a.onFirstInit) === null || _b === void 0 ? void 0 : _b.bind(this.script));
    }
    init() {
        var _a, _b;
        if (this.isDestroyed)
            delete this.isDestroyed;
        this.forwardFunctionCallToScript("onInit", (_b = (_a = this.script) === null || _a === void 0 ? void 0 : _a.onInit) === null || _b === void 0 ? void 0 : _b.bind(this.script), undefined, {
            onSuccess: () => {
                this.initPersistentVariables();
                this.isInitialized = true;
            }
        });
    }
    start() {
        if (this.script === undefined) {
            console.warn("[scripting] Script is undefined!");
            return;
        }
        this.setCurrentContext();
        if (this.script.onStart !== undefined) {
            try {
                this.recordFunctionCall("onStart");
                this.resolvePromiseReturnValue(this.script.onStart());
            }
            catch (error) {
                this.logError(error);
            }
        }
        try {
            if (this.callAfterStart.size > 0) {
                for (const delayedFnInfo of this.callAfterStart.values()) {
                    delayedFnInfo.fn.apply(this, delayedFnInfo.args);
                }
                this.callAfterStart.clear();
            }
        }
        catch (error) {
            this.logError(error);
        }
    }
    destroy() {
        var _a, _b;
        this.isDestroyed = true;
        this.onDestroyDelegate.invoke(this);
        this.onDestroyDelegate.clear();
        this.unsubscribeAllBusMessage();
        this.forwardFunctionCallToScript("onDestroy", (_b = (_a = this.script) === null || _a === void 0 ? void 0 : _a.onDestroy) === null || _b === void 0 ? void 0 : _b.bind(this.script), undefined, {
            onBeforeFunctionCall: () => {
                var _a;
                if (((_a = this.script) === null || _a === void 0 ? void 0 : _a.onUpdate) !== undefined) {
                    this.room.unregisterUpdateCallback(this);
                }
                return true;
            },
            onFinally: () => {
                this.callAfterStart.clear();
                this.script = undefined;
                this.isRegisteredInRoom = false;
            }
        });
    }
    setVisible(visible) {
        var _a, _b;
        if (this.isDestroyed)
            return;
        this.forwardFunctionCallToScript("onVisible", (_b = (_a = this.script) === null || _a === void 0 ? void 0 : _a.onVisible) === null || _b === void 0 ? void 0 : _b.bind(this.script), undefined, {
            onBeforeFunctionCall: () => {
                if (this.script === undefined)
                    return false;
                if (this.script.onVisible === undefined)
                    return false;
                if (!this.isInitialized && !this.isDestroyed) {
                    this.queueFunction(this.setVisible, visible);
                    return false;
                }
                return true;
            }
        });
    }
    onEnterRoom() {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onEnterRoom === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onEnterRoom);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onEnterRoom");
            this.resolvePromiseReturnValue(this.script.onEnterRoom());
        }
        catch (error) {
            this.logError(error);
        }
    }
    onLeaveRoom() {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onLeaveRoom === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onLeaveRoom);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onLeaveRoom");
            this.resolvePromiseReturnValue(this.script.onLeaveRoom());
        }
        catch (error) {
            this.logError(error);
        }
    }
    onClick(msg) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onClick === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onClick, msg);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onClick");
            this.resolvePromiseReturnValue(this.script.onClick({
                worldPoint: tmpVector31.fromArray(msg.hitPoint),
                localPoint: tmpVector32.fromArray(msg.localPoint),
                controllerUid: msg.controllerUid
            }));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onRemoteClick(msg) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onRemoteClick === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onRemoteClick, msg);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onRemoteClick");
            this.resolvePromiseReturnValue(this.script.onRemoteClick(tmpVector31.fromArray(msg.hitPoint)));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onSelect(selected, controllerUid, clientId) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onSelect, selected, controllerUid, clientId);
            return;
        }
        this.setCurrentContext();
        try {
            if (selected) {
                if (this.script.onSelect !== undefined) {
                    this.recordFunctionCall("onSelect");
                    this.resolvePromiseReturnValue(this.script.onSelect(controllerUid, clientId));
                }
            }
            else {
                if (this.script.onDeselect !== undefined) {
                    this.recordFunctionCall("onDeselect");
                    this.resolvePromiseReturnValue(this.script.onDeselect(controllerUid, clientId));
                }
            }
        }
        catch (error) {
            this.logError(error);
        }
    }
    onEnterContact(otherUid) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onEnterContact === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onEnterContact, otherUid);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onEnterContact");
            this.resolvePromiseReturnValue(this.script.onEnterContact(otherUid));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onLeaveContact(otherUid) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onLeaveContact === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onLeaveContact, otherUid);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onLeaveContact");
            this.resolvePromiseReturnValue(this.script.onLeaveContact(otherUid));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onButton(uid, input, type) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onButton === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onButton, uid, input, type);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onButton");
            this.resolvePromiseReturnValue(this.script.onButton(uid, input, type));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onInputAxis(uid, type, value) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onInputAxis === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onInputAxis, uid, type, value);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onInputAxis");
            this.resolvePromiseReturnValue(this.script.onInputAxis(uid, type, value));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onPrimaryButton(inputUid) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onPrimaryButton === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onPrimaryButton, inputUid);
            return;
        }
        try {
            this.recordFunctionCall("onPrimaryButton");
            this.resolvePromiseReturnValue(this.script.onPrimaryButton(inputUid));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onSecondaryButton(inputUid) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onSecondaryButton === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onSecondaryButton, inputUid);
            return;
        }
        try {
            this.recordFunctionCall("onSecondaryButton");
            this.resolvePromiseReturnValue(this.script.onSecondaryButton(inputUid));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onTertiaryButton(inputUid) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onTertiaryButton === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onTertiaryButton, inputUid);
            return;
        }
        try {
            this.recordFunctionCall("onTertiaryButton");
            this.resolvePromiseReturnValue(this.script.onTertiaryButton(inputUid));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onQuaternaryButton(inputUid) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onQuaternaryButton === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onQuaternaryButton, inputUid);
            return;
        }
        try {
            this.recordFunctionCall("onQuaternaryButton");
            this.resolvePromiseReturnValue(this.script.onQuaternaryButton(inputUid));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onPickup(controllerUid, isChild) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onPickup === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onPickup, controllerUid, isChild);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onPickup");
            this.resolvePromiseReturnValue(this.script.onPickup(controllerUid, isChild));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onDrop(controllerUid) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onDrop === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onDrop, controllerUid);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onDrop");
            this.resolvePromiseReturnValue(this.script.onDrop(controllerUid));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onOtherPickup(uid, controllerUid, isChild) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onOtherPickup === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onOtherPickup, uid, controllerUid, isChild);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onOtherPickup");
            this.resolvePromiseReturnValue(this.script.onOtherPickup(uid, controllerUid, isChild));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onOtherDrop(uid, controllerUid) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onOtherDrop === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onOtherDrop, uid, controllerUid);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onOtherDrop");
            this.resolvePromiseReturnValue(this.script.onOtherDrop(uid, controllerUid));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onGrabPickup() {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onGrabPickup === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onGrabPickup);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onGrabPickup");
            this.resolvePromiseReturnValue(this.script.onGrabPickup());
        }
        catch (error) {
            this.logError(error);
        }
    }
    onGrabReleased() {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onGrabReleased === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onGrabReleased);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onGrabReleased");
            this.resolvePromiseReturnValue(this.script.onGrabReleased());
        }
        catch (error) {
            this.logError(error);
        }
    }
    onOtherGrabPickup(uid) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onOtherGrabPickup === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onOtherGrabPickup, uid);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onOtherGrabPickup");
            this.resolvePromiseReturnValue(this.script.onOtherGrabPickup(uid));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onOtherGrabReleased(uid) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onOtherGrabReleased === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onOtherGrabReleased, uid);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onOtherGrabReleased");
            this.resolvePromiseReturnValue(this.script.onOtherGrabReleased(uid));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onTrainingVideoStateChanged(trainingVideoId, newState) {
        var _a, _b;
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onTrainingVideoStateChanged === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onTrainingVideoStateChanged, trainingVideoId, newState);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onTrainingVideoStateChanged");
            this.resolvePromiseReturnValue((_b = (_a = this.script).onTrainingVideoStateChanged) === null || _b === void 0 ? void 0 : _b.call(_a, trainingVideoId, newState));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onTrainingStageChanged(oldStageId, newStageId) {
        var _a, _b;
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onTrainingStageChanged === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onTrainingStageChanged, oldStageId, newStageId);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onTrainingStageChanged");
            this.resolvePromiseReturnValue((_b = (_a = this.script).onTrainingStageChanged) === null || _b === void 0 ? void 0 : _b.call(_a, oldStageId, newStageId));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onTrainingFinished(isRevisitMode) {
        var _a, _b;
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onTrainingFinished === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onTrainingFinished, isRevisitMode);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onTrainingFinished");
            this.resolvePromiseReturnValue((_b = (_a = this.script).onTrainingFinished) === null || _b === void 0 ? void 0 : _b.call(_a, isRevisitMode));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onHostChanged(isRoomHost) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onHostChanged === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed)
            return;
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onHostChanged");
            this.resolvePromiseReturnValue(this.script.onHostChanged(isRoomHost));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onUpdate(deltaTime) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onUpdate === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onUpdate");
            this.resolvePromiseReturnValue(this.script.onUpdate(deltaTime));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onTransformUpdate(localTransform, globalTransform) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onTransformUpdate === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onTransformUpdate, localTransform.clone(), globalTransform.clone());
            return;
        }
        this.setCurrentContext();
        const localTransformCopy = localTransform.clone();
        const globalTransformCopy = globalTransform.clone();
        try {
            this.recordFunctionCall("onTransformUpdate");
            this.resolvePromiseReturnValue(this.script.onTransformUpdate(localTransformCopy, globalTransformCopy));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onRPC(funcNameHash, ...params) {
        if (this.isDestroyed)
            return;
        if (!this.script)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onRPC, funcNameHash, params);
            return;
        }
        for (const param of params) {
            switch (typeof param) {
                case "boolean":
                case "number":
                case "string":
                case "undefined":
                    break;
                default:
                    console.error(`[scripting] onRPC: got param ${param} with not supported type ${typeof param}`);
                    return;
            }
        }
        let funcName = this.context.scriptFuncHashToName.get(funcNameHash);
        if (funcName === undefined) {
            funcName = this.getFuncNameByHash(funcNameHash);
            if (!funcName) {
                console.error(`[scripting] onRPC: can't find func name for hash ${funcNameHash}`);
                return;
            }
            this.context.scriptFuncHashToName.set(funcNameHash, funcName);
        }
        this.setCurrentContext();
        try {
            const func = this.script[funcName];
            if (typeof func !== "function") {
                console.error(`[scripting] onRPC: got funcName ${funcName} that is not a function`);
                return;
            }
            if (SCRIPT_FUNCTION_NAMES_SET.has(funcName)) {
                return;
            }
            this.recordFunctionCall(funcName);
            func.call(this.script, ...params);
        }
        catch (error) {
            this.logError(error);
        }
    }
    onClientConnect(clientId) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onClientConnect === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onClientConnect, clientId);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onClientConnect");
            this.resolvePromiseReturnValue(this.script.onClientConnect(clientId));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onClientDisconnect(clientId) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onClientDisconnect === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onClientDisconnect, clientId);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onClientDisconnect");
            this.resolvePromiseReturnValue(this.script.onClientDisconnect(clientId));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onPlayerSpawned() {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onPlayerSpawned === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onPlayerSpawned);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onPlayerSpawned");
            this.resolvePromiseReturnValue(this.script.onPlayerSpawned());
        }
        catch (error) {
            this.logError(error);
        }
    }
    onPlayerEntersRoom(avatarUid, clientId) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onPlayerEntersRoom === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onPlayerEntersRoom, avatarUid, clientId);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onPlayerEntersRoom");
            this.resolvePromiseReturnValue(this.script.onPlayerEntersRoom(avatarUid, clientId));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onPlayerLeavesRoom(avatarUid, clientId) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onPlayerLeavesRoom === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onPlayerLeavesRoom, avatarUid, clientId);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onPlayerLeavesRoom");
            this.resolvePromiseReturnValue(this.script.onPlayerLeavesRoom(avatarUid, clientId));
        }
        catch (error) {
            this.logError(error);
        }
    }
    onWebXrPresentingChanged() {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onWebXrPresentingChanged === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onWebXrPresentingChanged);
            return;
        }
        this.setCurrentContext();
        try {
            this.recordFunctionCall("onWebXrPresentingChanged");
            this.resolvePromiseReturnValue(this.script.onWebXrPresentingChanged());
        }
        catch (error) {
            this.logError(error);
        }
    }
    onXrControllerConnected(controllerUid, controllerHand) {
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (this.script.onXrControllerConnected === undefined)
            return;
        if (!this.isInitialized && !this.isDestroyed) {
            this.queueFunction(this.onXrControllerConnected, controllerUid, controllerHand);
            return;
        }
        this.setCurrentContext();
        try {
            this.resolvePromiseReturnValue(this.script.onXrControllerConnected(controllerUid, controllerHand));
        }
        catch (error) {
            this.logError(error);
        }
    }
    subscribeBusMessage(topic, callback) {
        if (!this.busSubscriptions) {
            this.busSubscriptions = new Set();
        }
        this.busSubscriptions.add(topic);
        scriptBusService.subscribeBusMessage(this.room, this.scriptIdx, topic, callback);
        return true;
    }
    unsubscribeBusMessage(topic) {
        if (!this.busSubscriptions)
            return false;
        this.busSubscriptions.delete(topic);
        scriptBusService.unsubscribeBusMessage(this.scriptIdx, topic);
        return true;
    }
    unsubscribeAllBusMessage() {
        if (!this.busSubscriptions)
            return;
        for (const topic of this.busSubscriptions) {
            scriptBusService.unsubscribeBusMessage(this.scriptIdx, topic);
        }
        this.busSubscriptions.clear();
        this.busSubscriptions = undefined;
    }
    getFuncNameByHash(hash) {
        if (!this.script)
            return;
        const prototype = Reflect.getPrototypeOf(this.script);
        if (!prototype)
            return undefined;
        const funcNames = Reflect.ownKeys(prototype);
        for (const funcName of funcNames) {
            if (typeof funcName !== "string")
                continue;
            if (funcName === "constructor")
                continue;
            const func = this.script[funcName];
            if (typeof func !== "function")
                continue;
            if (SCRIPT_FUNCTION_NAMES_SET.has(funcName))
                continue;
            if (hashCode(funcName) !== hash)
                continue;
            return funcName;
        }
        return undefined;
    }
    validateDefaultProperty(property, value) {
        const type = typeof property;
        if (type === "object") {
            if (Array.isArray(property)) {
                return this.validateArrayDefaultProperty(property, value);
            }
            else if (property instanceof ScriptModel) {
                return this.validateModelDefaultProperty(property, value);
            }
            else if (property instanceof Property) {
                if (property.isValidType(value)) {
                    return { value };
                }
                else {
                    return true;
                }
            }
            else if (property instanceof Vector3) {
                return this.validateMathProperty(property, value, 3);
            }
            else if (property instanceof Vector2) {
                return this.validateMathProperty(property, value, 2);
            }
            else if (property instanceof Quaternion) {
                return this.validateMathProperty(property, value, 4);
            }
            else if (property instanceof Vector4) {
                return this.validateMathProperty(property, value, 4);
            }
            else if (property instanceof Color) {
                return this.validateColorProperty(property, value);
            }
            console.warn(`[scripting] validateDefaultProperty: property value ${value} not supported`);
            return false;
        }
        if (VALID_DEFAULT_PROPERTY_TYPES.includes(type)) {
            const valueType = typeof value;
            if (VALID_DEFAULT_PROPERTY_TYPES.includes(valueType)) {
                return { value };
            }
            console.warn(`[scripting] validateDefaultProperty: value ${value} with typeof ${valueType} isn't supported`);
        }
        console.warn(`[scripting] validateDefaultProperty: property value ${value} for default type ${type} isn't supported`);
        return false;
    }
    validateMathProperty(property, value, mustArrayLength) {
        if (!value.startsWith("[")) {
            value = `[${value}]`;
        }
        try {
            const valueObj = JSON.parse(value);
            if (!Array.isArray(valueObj) && valueObj.length !== mustArrayLength) {
                console.warn(`[scripting] validateDefaultProperty: property value ${value} not supported`);
                return false;
            }
            property.fromArray(valueObj);
        }
        catch (_a) {
            console.warn(`[scripting] validateDefaultProperty: property value ${value} not supported`);
            return false;
        }
        return true;
    }
    validateColorProperty(property, value) {
        let valueObj = value;
        if (typeof valueObj === "string") {
            if (!valueObj.startsWith("[")) {
                valueObj = `[${valueObj}]`;
            }
            try {
                valueObj = JSON.parse(valueObj);
            }
            catch (_a) {
                console.warn(`[scripting] validateDefaultProperty: property value ${value} not supported`);
                return false;
            }
        }
        if (!Array.isArray(valueObj) || valueObj.length < 3) {
            console.warn(`[scripting] validateDefaultProperty: property value ${value} not supported`);
            return false;
        }
        // Color uses RGB; incoming RGBA is accepted and alpha is ignored.
        property.fromArray(valueObj);
        return true;
    }
    validateArrayDefaultProperty(property, value) {
        if (Array.isArray(value)) {
            property.length = value.length;
            for (let i = 0, len = value.length; i < len; ++i) {
                const element = value[i];
                const eleType = typeof element;
                if (VALID_DEFAULT_PROPERTY_TYPES.includes(eleType)) {
                    property[i] = value[i];
                }
                else {
                    property[i] = undefined;
                }
            }
            return true;
        }
        else if (typeof property !== "undefined") {
            property.length = 0;
            return true;
        }
        return false;
    }
    validateModelDefaultProperty(property, value) {
        ;
        property._connection = this.context.connection;
        property._context = this.context;
        const valueType = typeof value;
        if (valueType === "string") {
            const target = getModelFromAbsolutePath(value);
            if (target !== undefined) {
                property.modelUID = target.getUID();
                return true;
            }
        }
        else if (valueType === "number") {
            const target = value;
            if (target !== undefined) {
                property.modelUID = target;
                return true;
            }
        }
        return false;
    }
    scriptChanged(scriptInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            this.destroy();
            if (this.isDestroyed) {
                delete this.isDestroyed;
            }
            const ModelScriptConstructor = yield this.context.scriptManager
                .getModelScript(this.scriptId, true, this.modelUID, this.room.onLoadScript, this.room.onLoadedScript)
                .catch((error) => {
                console.error(`[scripting] Model script error: ${String(error)}`);
                return undefined;
            });
            // model destroyed by loading
            if (this.isDestroyed)
                return;
            if (ModelScriptConstructor === undefined) {
                console.warn("[scripting] Model-constructor is undefined! Model= ", this.modelUID);
                return;
            }
            this.script = new ModelScriptConstructor(this.modelUID);
            new ModelScriptFunctions(this.context, this.modelUID, this.script, this.room, this.scriptId, this.scriptIdx);
            if (scriptInfo !== undefined) {
                this.updateProperties(scriptInfo);
            }
            if (this.autoExecute || this.active) {
                this.registerScriptInRoom(this.script);
            }
        });
    }
    /**
     * Re-instantiates the script so it starts again from its initial state (`onInit`/`onStart` are called again).
     */
    reset(scriptInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isInitialized = false;
            yield this.scriptChanged(scriptInfo);
        });
    }
    registerScriptInRoom(script) {
        if (this.isRegisteredInRoom)
            return;
        if (this.room.isActive && this.room.isLayoutCreationFinished) {
            this.room.scriptsToBeInitialized.push(this);
            this.room.scriptsToBeActivated.push(this);
        }
        if (script.onUpdate !== undefined) {
            this.room.registerUpdateCallback(this);
        }
        this.isRegisteredInRoom = true;
    }
    updateProperties(scriptInfo) {
        if (scriptInfo.properties === undefined)
            return;
        if (this.script === undefined)
            return;
        for (const name in scriptInfo.properties) {
            if (!this.script.hasOwnProperty(name))
                continue;
            const propInfo = scriptInfo.properties[name];
            if (!propInfo)
                continue;
            const value = propInfo.value;
            const property = this.script[name];
            let result;
            switch (propInfo.type) {
                case ScriptPropertyType.Property:
                    result = this.validateDefaultProperty(property, value);
                    break;
                case ScriptPropertyType.Model:
                    if (property instanceof ScriptModel) {
                        result = this.validateModelDefaultProperty(property, value);
                    }
                    break;
            }
            if (result === undefined || result === false || result === true) {
                continue;
            }
            ;
            this.script[name] = result.value;
        }
    }
    setPersistentVariable(variableName) {
        if (!this.persistentVariables) {
            this.persistentVariables = new Map();
        }
        this.persistentVariables.set(variableName, {
            value: undefined,
            needsUpdate: false
        });
        if (this.script) {
            if (!this.script.hasOwnProperty(variableName)) {
                throw new Error(`persistent variable ${variableName} isn't defined`);
            }
            const propertyValue = this.script[variableName];
            Object.defineProperty(this.script, variableName, {
                get: () => {
                    return propertyValue;
                },
                set: () => {
                    throw new Error(`persistent variable ${variableName} can't be changed in init function`);
                }
            });
        }
    }
    initPersistentVariables() {
        if (this.isDestroyed)
            return;
        if (!this.script)
            return;
        if (!this.persistentVariables)
            return;
        const model = modelPool.getModel(this.modelUID);
        let persistentData = model === null || model === void 0 ? void 0 : model.getFacet(PersistentDataFacet);
        if (!persistentData) {
            persistentData = new PersistentDataFacet({});
        }
        let data = persistentData.data;
        if (!data) {
            data = {};
            persistentData.data = data;
        }
        if (!data.variables) {
            data.variables = {};
        }
        for (const [variableName, propertyData] of this.persistentVariables) {
            if (!this.script.hasOwnProperty(variableName))
                continue;
            if (variableName in data.variables) {
                propertyData.value = data.variables[variableName];
            }
            else {
                propertyData.value = this.script[variableName];
                data.variables[variableName] = propertyData.value;
                this.persistentVariablesNeedsUpdate = true;
            }
            Object.defineProperty(this.script, variableName, {
                get: () => {
                    return propertyData.value;
                },
                set: (value) => {
                    propertyData.value = value;
                    propertyData.needsUpdate = true;
                    this.persistentVariablesNeedsUpdate = true;
                    if (this.isInitialized) {
                        this.room.persistentVariablesChanged.add(this);
                    }
                }
            });
        }
        if (this.persistentVariablesNeedsUpdate) {
            this.context.pendingMessages.push(new UpdateFacetOnModelMessage(this.modelUID, persistentData));
        }
    }
    syncPersistentVariables() {
        if (this.isDestroyed)
            return;
        if (!this.persistentVariables)
            return;
        if (!this.script)
            return;
        const model = modelPool.getModel(this.modelUID);
        const persistentData = model === null || model === void 0 ? void 0 : model.getFacet(PersistentDataFacet);
        if (persistentData === undefined)
            return;
        const data = persistentData.data;
        if (!data)
            return;
        if (!data.variables)
            return;
        for (const [variableName, propertyData] of this.persistentVariables) {
            if (!propertyData.needsUpdate)
                continue;
            propertyData.needsUpdate = false;
            if (!this.script.hasOwnProperty(variableName))
                continue;
            data.variables[variableName] = propertyData.value;
        }
        this.context.pendingMessages.push(new UpdateFacetOnModelMessage(this.modelUID, persistentData));
        this.persistentVariablesNeedsUpdate = false;
    }
    onUpdatePersistentVariables(facet) {
        if (!facet)
            return;
        if (this.isDestroyed)
            return;
        if (this.script === undefined)
            return;
        if (!this.persistentVariables)
            return;
        const data = facet.data;
        if (!data)
            return;
        if (!data.variables)
            return;
        for (const variableName in data.variables) {
            if (!this.script.hasOwnProperty(variableName))
                continue;
            const propertyData = this.persistentVariables.get(variableName);
            if (propertyData) {
                propertyData.value = data.variables[variableName];
                propertyData.needsUpdate = false;
            }
        }
    }
    /// Helper methods ///
    // ToDo: Right now we only remember the last function call of the same function type. this might cause problems, but
    //  we want to avoid that millions of function calls get stored. One option might be to handle the function calls
    //  case by case. For some function calls we might want all messages while for others only the most recent info is
    //  only relevant
    queueFunction(fn, ...args) {
        this.callAfterStart.set(fn.name, { fn, args });
    }
    recordFunctionCall(functionName) {
        scriptService.recordScriptFunctionCall(this, functionName);
    }
    forwardFunctionCallToScript(functionName, fn, args, options) {
        var _a, _b, _c, _d;
        if (this.script === undefined) {
            return false;
        }
        this.setCurrentContext();
        let status;
        try {
            if (((_a = options === null || options === void 0 ? void 0 : options.onBeforeFunctionCall) === null || _a === void 0 ? void 0 : _a.call(options)) === false)
                return false;
            if (fn !== undefined) {
                this.recordFunctionCall(functionName);
                this.resolvePromiseReturnValue(fn(...args !== null && args !== void 0 ? args : []));
            }
            (_b = options === null || options === void 0 ? void 0 : options.onSuccess) === null || _b === void 0 ? void 0 : _b.call(options);
            status = true;
        }
        catch (error) {
            this.logError(error);
            (_c = options === null || options === void 0 ? void 0 : options.onFailure) === null || _c === void 0 ? void 0 : _c.call(options);
            status = false;
        }
        (_d = options === null || options === void 0 ? void 0 : options.onFinally) === null || _d === void 0 ? void 0 : _d.call(options);
        return status;
    }
    resolvePromiseReturnValue(returnValue) {
        if (isPromise(returnValue)) {
            Promise.resolve(returnValue).catch((error) => {
                this.logError(error);
            });
        }
    }
    logError(error) {
        var _a, _b;
        const scriptName = this.script !== undefined ? this.script.constructor.name : "undefined";
        const modelName = (_b = (_a = modelPool.getModel(this.modelUID)) === null || _a === void 0 ? void 0 : _a.getName()) !== null && _b !== void 0 ? _b : "unknown";
        console.error(`[scripting] Error in Script ${scriptName} with model ${modelName}`, error);
    }
}
